import type { ClaimAsLabModalProps } from '../Page/OrderManagementPage/Modals/ClaimAsLabModal.js'
import { ClaimAsLabModal } from '../Page/OrderManagementPage/Modals/ClaimAsLabModal.js'
import type { SubmitOrderModalProps } from '../Page/OrderManagementPage/Modals/OrderSubmitModal.js'
import { SubmitOrderModal } from '../Page/OrderManagementPage/Modals/OrderSubmitModal.js'
import type React from 'react'
import type { BaseModalProps } from './BaseModal.js'
import type { PipelineInformationModalProps } from '../Page/OrderManagementPage/Modals/PipelineInformationModal.js'
import { PipelineInformationModal } from '../Page/OrderManagementPage/Modals/PipelineInformationModal.js'
import type { ReportEmailModalProps } from '../Page/OrderManagementPage/Modals/ReportEmailModal.js'
import { ReportEmailModal } from '../Page/OrderManagementPage/Modals/ReportEmailModal.js'
import type { ImportModalProps } from '../Page/OrderManagementPage/ImportSamplesModal.js'
import { ImportSamplesModal } from '../Page/OrderManagementPage/ImportSamplesModal.js'
import type { InviteLabModalProps } from '../Page/OrderManagementPage/Modals/InviteLabModal.js'
import { InviteLabModal } from '../Page/OrderManagementPage/Modals/InviteLabModal.js'
import type { MultiQCModalProps } from '../Page/OrderManagementPage/Modals/MultiQCModal.js'
import { MultiQCModal } from '../Page/OrderManagementPage/Modals/MultiQCModal.js'
import type { FileModalProps } from '../Page/OrderManagementPage/Modals/FileModal.js'
import { FileModal } from '../Page/OrderManagementPage/Modals/FileModal.js'
import type { PrimerNotFoundModalProps } from '../Page/OrderManagementPage/Modals/PrimerNotFoundModal.js'
import { PrimerNotFoundModal } from '../Page/OrderManagementPage/Modals/PrimerNotFoundModal.js'
import type { PrimerAmpliconMismatchModalProps } from '../Page/OrderManagementPage/Modals/PrimerAmpliconMismatchModal.js'
import { PrimerAmpliconMismatchModal } from '../Page/OrderManagementPage/Modals/PrimerAmpliconMismatchModal.js'
import type { PrimerChoiceModalProps } from '../Page/OrderManagementPage/Modals/PrimerChoiceModal.js'
import { PrimerChoiceModal } from '../Page/OrderManagementPage/Modals/PrimerChoiceModal.js'
import { PipelineSettingsWidget } from '../Common/PipelineSettingsWidget.js'
import { VersionWidget } from '../Common/VersionWidget.js'
import { SystemNotificationsWidget } from '../Common/SystemNotificationsWidget.js'
import { WelcomeFreshUser } from '../Common/WelcomeFreshUser.js'
import { UserMustSignupModal } from '../Component/SignupNudge/UserMustSignupModal.js'
import { SubmitInfoModal } from '../Page/OrderManagementPage/OrderDetail/SubmitInfoModal.js'
import type { EditOrderModalProps } from '../Page/OrderManagementPage/EditOrderModal.js'
import { EditOrderModal } from '../Page/OrderManagementPage/EditOrderModal.js'
import type { KronaModalProps } from '../Page/OrderManagementPage/Modals/KronaModal.js'
import { KronaModal } from '../Page/OrderManagementPage/Modals/KronaModal.js'
import { CreateProjectModal } from '../Page/ProjectPage/CreateProjectModal.js'
import type { EditProjectModalProps } from '../Page/ProjectPage/EditProjectModal.js'
import { EditProjectModal } from '../Page/ProjectPage/EditProjectModal.js'
import type { ImportSamplesToProjectModalProps } from '../Page/ProjectPage/ImportSamplesToProjectModal.js'
import { ImportSamplesToProjectModal } from '../Page/ProjectPage/ImportSamplesToProjectModal.js'
import type { InviteParticipantModalProps } from '../Page/ProjectPage/InviteParticipantModal.js'
import { InviteParticipantModal } from '../Page/ProjectPage/InviteParticipantModal.js'
import type { ProjectReportModalProps } from '../Page/ProjectPage/ProjectReportModal.js'
import { ProjectReportModal } from '../Page/ProjectPage/ProjectReportModal.js'
import type { OrderReportModalProps } from '../Page/OrderManagementPage/Modals/OrderReportModal.js'
import { OrderReportModal } from '../Page/OrderManagementPage/Modals/OrderReportModal.js'
import type { SampleConfigurationTypeModalProps } from '../Page/Registration/SampleConfigurationTypeModal.js'
import { SampleConfigurationTypeModal } from '../Page/Registration/SampleConfigurationTypeModal.js'
import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.js'
import type { ParseCSVModalProps, ParseCSVModalResult } from '../Page/HomePage/Modals/ParseCSVModal.js'
import { ParseCSVModal } from '../Page/HomePage/Modals/ParseCSVModal.js'
import type { StartPostprocessingModalProps } from '../Page/OrderManagementPage/Modals/StartPostprocessingModal.js'
import { StartPostprocessingModal } from '../Page/OrderManagementPage/Modals/StartPostprocessingModal.js'
import type { EditPoolCsvModalProps, EditPoolCsvModalResult } from '../Page/HomePage/Modals/EditPoolCsvModal.tsx'
import { EditPoolCsvModal } from '../Page/HomePage/Modals/EditPoolCsvModal.tsx'
import { MarkusAdminModal } from '../Page/Admin/MarkusAdminModal.js'

/**
 * This file contains the mapping of modal types to their respective components.
 * It is used to ensure that the modal types are correctly mapped to their respective components.
 * Furthermore, with enough TS magic, props and optional return types of modals are correctly inferred at the call
 * sites.
 *
 * A fresh modal has to be added once in the ModalType type and in the MODAL_COMPONENTS structure at the bottom.
 */

export type ModalType =
  | { type: 'claimAsLab'; props: ClaimAsLabModalProps }
  | { type: 'SubmitOrder'; props: SubmitOrderModalProps }
  | { type: 'pipelineInformation'; props: PipelineInformationModalProps }
  | { type: 'reportEmail'; props: ReportEmailModalProps }
  | { type: 'importSamples'; props: ImportModalProps }
  | { type: 'inviteLab'; props: InviteLabModalProps }
  | { type: 'multiqc'; props: MultiQCModalProps }
  | { type: 'file'; props: FileModalProps }
  | { type: 'primersNotFound'; props: PrimerNotFoundModalProps }
  | { type: 'primerAmpliconMismatch'; props: PrimerAmpliconMismatchModalProps }
  | { type: 'primerChoice'; props: PrimerChoiceModalProps }
  | { type: 'pipelineSettings'; props: object }
  | { type: 'changelog'; props: object }
  | { type: 'systemNotifications'; props: object }
  | { type: 'freshUser'; props: object }
  | { type: 'signupNudge'; props: object }
  | { type: 'submitInfo'; props: object }
  | { type: 'editOrder'; props: EditOrderModalProps }
  | { type: 'krona'; props: KronaModalProps }
  | { type: 'createProject'; props: object; result: string }
  | { type: 'editProject'; props: EditProjectModalProps }
  | { type: 'importSamplesProject'; props: ImportSamplesToProjectModalProps }
  | { type: 'inviteParticipant'; props: InviteParticipantModalProps }
  | { type: 'projectReport'; props: ProjectReportModalProps }
  | { type: 'orderReport'; props: OrderReportModalProps }
  | { type: 'sampleConfigurationType'; props: SampleConfigurationTypeModalProps; result: SampleConfigurationTypeKey }
  | { type: 'parseCSV'; props: ParseCSVModalProps; result: ParseCSVModalResult }
  | { type: 'startPostprocessing'; props: StartPostprocessingModalProps }
  | { type: 'editCsvModal'; props: EditPoolCsvModalProps; result: EditPoolCsvModalResult }
  | { type: 'markusAdmin'; props: object }

export type ModalTypeName = ModalType['type']

export type ModalPropsMap = {
  [M in ModalType as M['type']]: M['props']
}

export type ModalResultMap = {
  [M in ModalType as M['type']]: M extends { result: infer R } ? R : void
}

type ModalComponentMap = {
  [K in ModalTypeName]: React.FC<ModalPropsMap[K] & BaseModalProps<ModalResultMap[K]>>
}

// Helper to enforce the type
export function createModalRegistry<T extends ModalComponentMap>(registry: T): T {
  return registry
}

export const MODAL_COMPONENTS = createModalRegistry({
  claimAsLab: (props) => ClaimAsLabModal(props),
  SubmitOrder: (props) => SubmitOrderModal(props),
  pipelineInformation: (props) => PipelineInformationModal(props),
  reportEmail: (props) => ReportEmailModal(props),
  importSamples: (props) => ImportSamplesModal(props),
  inviteLab: (props) => InviteLabModal(props),
  file: (props) => FileModal(props),
  multiqc: (props) => MultiQCModal(props),
  primersNotFound: (props) => PrimerNotFoundModal(props),
  primerAmpliconMismatch: (props) => PrimerAmpliconMismatchModal(props),
  primerChoice: (props) => PrimerChoiceModal(props),
  pipelineSettings: (props) => PipelineSettingsWidget(props),
  changelog: (props) => VersionWidget(props),
  systemNotifications: (props) => SystemNotificationsWidget(props),
  freshUser: (props) => WelcomeFreshUser(props),
  signupNudge: (props) => UserMustSignupModal(props),
  submitInfo: (props) => SubmitInfoModal(props),
  editOrder: (props) => EditOrderModal(props),
  krona: (props) => KronaModal(props),
  createProject: (props) => CreateProjectModal(props),
  editProject: (props) => EditProjectModal(props),
  importSamplesProject: (props) => ImportSamplesToProjectModal(props),
  inviteParticipant: (props) => InviteParticipantModal(props),
  projectReport: (props) => ProjectReportModal(props),
  orderReport: (props) => OrderReportModal(props),
  sampleConfigurationType: (props) => SampleConfigurationTypeModal(props),
  parseCSV: (props) => ParseCSVModal(props),
  startPostprocessing: (props) => StartPostprocessingModal(props),
  editCsvModal: (props) => EditPoolCsvModal(props),
  markusAdmin: (props) => MarkusAdminModal(props)
})
